import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDemoServices } from "../lib/DemoContext.jsx";
import { createCourseClient } from "../lib/courseClient.js";
import { TextCourseClient } from "../lib/textCourseClient.js";
import { ARCHIVE_RETRY_MESSAGE } from "../lib/courseArchive.js";
import { Composer, ConversationThread } from "./Chat.jsx";

const GENDERS = ["女", "男", "其他"];
const MODALITIES = [
  ["bt", "行为治疗（BT）"], ["cbt", "认知行为治疗（CBT）"], ["het", "人本存在治疗（HET）"],
  ["pdt", "心理动力治疗（PDT）"], ["pmt", "家庭治疗（PMT）"],
];
const STATUS_LABELS = {
  select: "请选择来访者", creating: "正在建档", connecting: "正在连接", opening: "等待咨询师开场",
  ready: "可以输入", thinking: "咨询师正在思考", speaking: "咨询师正在回复", archiving: "正在归档",
  archive_retry: "归档待确认，请重试", archive_disconnected: "连接已断开，归档待核对",
  finished: "本次会话已归档", interrupted: "会话已中断", closed: "连接已关闭", error: "课程加载或连接失败",
};
const statusLabel = (status) => STATUS_LABELS[status] || "正在处理";

function courseFields(form, coursePromptVersion) {
  const traits = {};
  if (form.name) traits.name = form.name;
  if (form.gender) traits.gender = form.gender;
  if (form.age) traits.age = Number(form.age);
  const clientInfo = {};
  if (Object.keys(traits).length) clientInfo.static_traits = traits;
  if (form.mainProblem) clientInfo.main_problem = form.mainProblem;
  return {
    modality: form.modality,
    ...(coursePromptVersion ? { prompt_version: coursePromptVersion } : {}),
    client_name: form.name,
    ...(form.userId ? { user_id: form.userId } : {}),
    ...(Object.keys(clientInfo).length ? { client_info: clientInfo } : {}),
  };
}

function CoursePicker({ roster, busy, onRefresh, onAttach, onCreate, onNewTherapy, error, account = false, locale = "zh-CN" }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", userId: "", gender: "女", age: "", modality: "cbt", mainProblem: "" });
  const set = (patch) => setForm((value) => ({ ...value, ...patch }));
  return (
    <section className="text-course-picker">
      <p className="ap-hint">选择来访者后，咨询师会先自动开场。</p>
      <div className="ap-roster-head">
        <span>来访者课程</span>
        <button className="ap-btn small" disabled={busy} onClick={onRefresh}>刷新</button>
        <button className="ap-btn small primary" disabled={busy} onClick={() => account ? onNewTherapy?.() : setShowForm((open) => !open)}>{account ? (locale.startsWith("zh") ? "＋ 新建咨询" : "＋ New Therapy") : "＋ 新来访者"}</button>
      </div>
      {showForm && (
        <div className="ap-form">
          <input placeholder="称呼（必填）" value={form.name} onChange={(event) => set({ name: event.target.value })} />
          <input placeholder="用户 ID（可选）" value={form.userId} onChange={(event) => set({ userId: event.target.value })} />
          <div className="ap-form-row">
            <div className="seg">{GENDERS.map((gender) => <button key={gender} className={form.gender === gender ? "on" : ""} onClick={() => set({ gender })}>{gender}</button>)}</div>
            <input type="number" min="1" max="120" placeholder="年龄" value={form.age} onChange={(event) => set({ age: event.target.value })} />
          </div>
          <label className="text-course-label">疗法形式
            <select value={form.modality} onChange={(event) => set({ modality: event.target.value })}>
              {MODALITIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <textarea className="ap-form-textarea" rows={3} placeholder="主要想咨询的问题（必填）" value={form.mainProblem} onChange={(event) => set({ mainProblem: event.target.value })} />
          <button className="ap-btn primary" disabled={busy || !form.name.trim() || !form.mainProblem.trim()} onClick={() => onCreate({ ...form, name: form.name.trim(), userId: form.userId.trim(), mainProblem: form.mainProblem.trim() })}>建档并开始</button>
        </div>
      )}
      {roster === null ? <div className="ap-empty">加载课程列表…</div> : !roster.length ? <div className="ap-empty">还没有来访者。</div> : roster.map((course) => (
        <div className="ap-client" key={course.course_id}>
          <div className="ap-client-name">{course.client_name || course.user_id || course.course_id}</div>
          <div className="ap-course-row">
            <span className="chip">第 {course.next_session_index || 1} 次</span>
            <span className="ap-course-date">{course.modality || "cbt"}</span>
            <button className="ap-btn small primary" disabled={busy} onClick={() => onAttach(course.course_id)}>开始 / 继续</button>
          </div>
        </div>
      ))}
      {error && <div className="ap-error">{error}</div>}
    </section>
  );
}

export default function TextCourse({ model, coursePromptVersion = "", thinking = true, initialCourseId = "", onProgress, onArchived, onNewTherapy, onExit, locale = "zh-CN" }) {
  const tr = (zh, en) => locale.startsWith("zh") ? zh : en;
  const label = status => locale.startsWith("zh") ? statusLabel(status) : ({
    select: "Choose a consultation", creating: "Creating", connecting: "Connecting", opening: "Waiting for the opening reply",
    ready: "Ready", thinking: "Thinking", speaking: "Responding", archiving: "Archiving",
    archive_retry: "Archive unconfirmed — retry", archive_disconnected: "Disconnected — archive unconfirmed",
    finished: "Session archived", interrupted: "Session interrupted", closed: "Disconnected", error: "Unable to connect",
  }[status] || "Preparing");
  const services = useDemoServices();
  const api = useMemo(() => services ? services.courseClient(model) : createCourseClient(model), [model, services]);
  const clientRef = useRef(null);
  const messageIds = useRef(new Map());
  const serial = useRef(0);
  const [roster, setRoster] = useState(null);
  const [courseId, setCourseId] = useState(initialCourseId);
  const [epoch, setEpoch] = useState(0);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState("select");
  const [session, setSession] = useState(null);
  const [finish, setFinish] = useState(null);
  const [archiveQueued, setArchiveQueued] = useState(false);
  const [error, setError] = useState("");
  const [contextWarning, setContextWarning] = useState(null);

  useEffect(() => { onProgress?.({ courseId, sessionIndex: session?.session_index, status, archiveQueued }); }, [courseId, session?.session_index, status, archiveQueued, onProgress]);

  const refresh = useCallback(async () => {
    try {
      const result = await api.list();
      setRoster(result.courses || []);
      setError("");
    } catch (reason) {
      setRoster([]);
      setError(`加载课程列表失败：${reason.message}`);
    }
  }, [api]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!services || initialCourseId) return;
    let alive = true;
    api.ensure().then(result => { if (alive) setCourseId(result.course_id); })
      .catch(error => { if (alive) setError(error.message); });
    return () => { alive = false; };
  }, [api, services, initialCourseId]);

  const updateMessage = useCallback((role, key, text, patch = {}) => {
    const id = `${role}:${key || "current"}`;
    const existing = messageIds.current.get(id);
    if (existing) {
      setMessages((current) => current.map((message) => message.id === existing
        ? { ...message, ...(text !== undefined ? { text } : {}), ...patch } : message));
      return;
    }
    const localId = ++serial.current;
    messageIds.current.set(id, localId);
    setMessages((current) => [...current, { id: localId, role, text: text ?? "", ...patch }]);
  }, []);

  useEffect(() => {
    if (!courseId) return undefined;
    messageIds.current = new Map();
    serial.current = 0;
    setMessages([]);
    setSession(null);
    setFinish(null);
    setArchiveQueued(false);
    setContextWarning(null);
    setError("");
    setStatus("connecting");
    const makeClient = services ? services.textCourseClient : options => new TextCourseClient(options);
    const client = makeClient({
      model, courseId, thinking,
      handlers: {
        onStatus: setStatus,
        onError: (message) => setError(message),
        onHistoryMessage: ({ role, id, text }) => updateMessage(role, id, text),
        onAssistantText: (text, done, id) => updateMessage("assistant", id, text, { streaming: !done }),
        onReasoning: (text, done, id) => { if (done && text) updateMessage("assistant", id, undefined, { reasoning: text }); },
        onAgentUpdate: (kind, data) => {
          if (kind === "session.start") setSession(data);
          if (kind === "session.finishing") setStatus("archiving");
          if (kind === "session.archive_queued") { setArchiveQueued(true); setStatus("archiving"); }
          if (kind === "session.finish") { setFinish(data); setStatus("finished"); refresh(); onArchived?.(); }
          if (kind === "session.interrupt") { if (onExit) onExit(); else { setCourseId(""); setStatus("select"); refresh(); } }
          if (kind === "end_signal") setSession((current) => ({ ...(current || {}), endSignal: true }));
          if (kind === "context.warning") setContextWarning(data);
        },
      },
    });
    clientRef.current = client;
    client.connect();
    return () => {
      client.stop();
      if (clientRef.current === client) clientRef.current = null;
    };
  }, [courseId, epoch, model, refresh, updateMessage]);

  useEffect(() => { clientRef.current?.setThinking(thinking); }, [thinking]);

  const create = async (form) => {
    setError("");
    setStatus("creating");
    try {
      const result = await api.create(courseFields(form, coursePromptVersion));
      if (!result.course_id) throw new Error(result.error || "未返回课程编号");
      setCourseId(result.course_id);
      refresh();
    } catch (reason) {
      setError(`课程创建失败：${reason.message}`);
      setStatus("select");
    }
  };
  const attach = (id) => { setCourseId(id); setEpoch((value) => value + 1); };
  const send = () => {
    const text = draft.trim();
    if (!text || !clientRef.current?.sendText(text)) return;
    updateMessage("user", `local-${serial.current + 1}`, text);
    setDraft("");
  };
  const restart = () => setEpoch((value) => value + 1);
  const nextSession = () => { setEpoch((value) => value + 1); };
  const backToRoster = () => { clientRef.current?.stop(); setCourseId(""); setStatus("select"); };
  const finishSession = () => {
    if (clientRef.current?.finishCourse()) setError("");
    else setError("当前无法提交归档，请检查连接或等待正在进行的归档完成。");
  };
  const exit = () => {
    clientRef.current?.stop();
    if (onExit) onExit();
    else { setCourseId(''); setStatus('select'); refresh(); }
  };
  const canSend = !!clientRef.current?.canSend && status === "ready" && !finish;
  const canFinish = !!session && !finish && !["archiving", "archive_disconnected", "closed", "error", "finished", "interrupted"].includes(status);
  const recoverableConnectionError = status === "closed" || status === "error";
  const composerPlaceholder = canSend ? tr("从此刻的想法开始…", "Start with what’s on your mind…") : label(status);

  if (!courseId) return <CoursePicker account={!!services} roster={roster} busy={status === "creating"} onRefresh={refresh} onAttach={attach} onCreate={create} onNewTherapy={onNewTherapy} locale={locale} error={error} />;
  return (
    <section className="text-course" aria-label={tr("文字课程", "Text consultation")}>
      <header className="text-course-head">
        <div><b>{session?.client_info?.static_traits?.name || tr("文字咨询", "Text consultation")}</b><span role="status">{tr(`第 ${session?.session_index || "…"} 次会话`, `Session ${session?.session_index || "…"}`)} · {label(status)}</span></div>
        <div className="text-course-actions">
          {recoverableConnectionError && <button className="ap-btn small" onClick={restart}>{tr("重新连接", "Reconnect")}</button>}
          {recoverableConnectionError && <button className="ap-btn small" onClick={backToRoster}>{tr("返回课程列表", "Back to consultations")}</button>}
          <button className="ap-btn small" disabled={!canFinish} onClick={finishSession}>{status === "archive_retry" ? tr("重试归档", "Retry archive") : tr("结束并归档", "Finish and archive")}</button>
          <button className="ap-btn small" disabled={!archiveQueued && ["archiving", "archive_retry", "archive_disconnected"].includes(status)} onClick={exit}>{tr("退出", "Exit consultation")}</button>
        </div>
      </header>
      {error && <div className="ap-error text-course-error" role="alert">{error}</div>}
      {status === "archive_retry" && <div className="ap-banner" role="status">{archiveQueued ? tr('后台归档尚未完成，可以退出后在咨询记录中查看或重试。', 'The background archive is not complete. You can leave and check or retry it in history.') : tr(ARCHIVE_RETRY_MESSAGE, "The archive is unconfirmed. Retry the same archive before continuing.")}</div>}
      {contextWarning && !finish && <div className="ap-banner" role="status">{contextWarning.message || tr("本次会话已接近上下文上限，请结束并归档后开始下一次会话。", "This session is nearing its context limit. Finish and archive before starting the next session.")}</div>}
      {session?.endSignal && !finish && <div className="ap-banner">{tr("模型给出了结束信号，可以归档本次会话。", "The conversation is ready to wrap up. You can archive this session.")}</div>}
      {status === "archiving" && <div className="ap-archiving" role="status">{archiveQueued ? tr("已交给后台归档，可以退出；进度见咨询记录。", "Archiving in the background. You can leave and check progress in history.") : tr("正在提交归档，请稍候…", "Submitting archive, please wait…")}</div>}
      <ConversationThread messages={messages} locale={locale} resetKey={`${courseId}:${epoch}`}
        emptyText={canSend ? tr("准备好了就开始，你可以按自己的节奏表达。", "Start whenever you’re ready. Take your time.") : label(status)} />
      {finish ? (
        <div className="text-course-finish"><div className="ap-banner ok" role="status">{tr("本次会话已归档。", "This session has been archived.")}</div><button className="ap-btn primary" onClick={nextSession}>{tr("开始下一次会话", "Start next session")}</button></div>
      ) : (
        <div className="text-course-composer">
          <Composer value={draft} disabled={!canSend} placeholder={composerPlaceholder}
            onChange={setDraft} onSend={send} locale={locale} />
        </div>
      )}
    </section>
  );
}
